import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Grid,
  GridItem,
  SimpleGrid,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  FaChevronRight,
  FaStethoscope,
  FaHospital,
  FaChartLine,
  FaStar,
  FaHandHoldingHeart,
  FaBalanceScale,
  FaGraduationCap,
  FaShieldAlt,
  FaClipboardCheck,
  FaMedal,
  FaUsers,
  FaCheck,
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import {
  useMotionAllowed,
  CountUp,
  FitHeadline,
  HeroRecede,
  EASE,
  Reveal,
  RevealGroup,
  RevealItem,
  ZoomSettle,
} from '../components/motion';
import { SectionHeading, IconMark, InkBand, LightSheet } from '../components/premium';
import { lightGlass, clearGlass } from '../components/glass';
import PhysicianPartnershipForm from '../components/forms/PhysicianPartnershipForm';

const MotionBox = motion(Box);

// ─── Animated SVG Components ────────────────────────────────────────

const HeartPulseSVG = ({ size = 64 }) => (
  <Box as="svg" viewBox="0 0 64 64" w={`${size}px`} h={`${size}px`}>
    {/* Heart shape */}
    <path
      d="M32,56 C32,56 8,40 8,24 C8,14 16,8 24,8 C28,8 31,10 32,14 C33,10 36,8 40,8 C48,8 56,14 56,24 C56,40 32,56 32,56Z"
      fill="none"
      stroke="#ef5350"
      strokeWidth="2.5"
      strokeLinejoin="round"
    >
      <animate attributeName="stroke-width" values="2.5;3;2.5" dur="1s" repeatCount="indefinite" />
    </path>
    <path
      d="M32,56 C32,56 8,40 8,24 C8,14 16,8 24,8 C28,8 31,10 32,14 C33,10 36,8 40,8 C48,8 56,14 56,24 C56,40 32,56 32,56Z"
      fill="#ef5350"
      opacity="0.15"
    >
      <animate attributeName="opacity" values="0.15;0.25;0.15" dur="1s" repeatCount="indefinite" />
    </path>
    {/* ECG line across heart */}
    <polyline
      points="12,32 22,32 26,22 30,42 34,28 38,36 42,32 52,32"
      fill="none"
      stroke="#ef5350"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <animate
        attributeName="stroke-dashoffset"
        from="120"
        to="0"
        dur="1.5s"
        repeatCount="indefinite"
      />
      <animate
        attributeName="stroke-dasharray"
        values="0,120;60,60;120,0"
        dur="1.5s"
        repeatCount="indefinite"
      />
    </polyline>
  </Box>
);

const SpineSVG = ({ size = 64 }) => (
  <Box as="svg" viewBox="0 0 64 64" w={`${size}px`} h={`${size}px`}>
    {[0, 1, 2, 3, 4].map((i) => (
      <g key={i}>
        <rect
          x="22"
          y={8 + i * 11}
          width="20"
          height="9"
          rx="4"
          fill="none"
          stroke="#26a69a"
          strokeWidth="2"
          opacity="0.9"
        >
          <animate
            attributeName="opacity"
            values="0.6;1;0.6"
            dur="2s"
            begin={`${i * 0.2}s`}
            repeatCount="indefinite"
          />
        </rect>
        {/* Disc between vertebrae */}
        {i < 4 && (
          <ellipse
            cx="32"
            cy={17 + i * 11}
            rx="8"
            ry="2"
            fill="#80cbc4"
            opacity="0.4"
          >
            <animate
              attributeName="opacity"
              values="0.3;0.5;0.3"
              dur="2s"
              begin={`${i * 0.2 + 0.1}s`}
              repeatCount="indefinite"
            />
          </ellipse>
        )}
      </g>
    ))}
  </Box>
);

const JointSVG = ({ size = 64 }) => (
  <Box as="svg" viewBox="0 0 64 64" w={`${size}px`} h={`${size}px`}>
    {/* Upper bone */}
    <path
      d="M24,8 L24,24 Q24,28 28,28 L36,28 Q40,28 40,24 L40,8"
      fill="none"
      stroke="#42a5f5"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    {/* Lower bone */}
    <path
      d="M24,56 L24,40 Q24,36 28,36 L36,36 Q40,36 40,40 L40,56"
      fill="none"
      stroke="#42a5f5"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    {/* Joint gap / cartilage */}
    <ellipse cx="32" cy="32" rx="12" ry="5" fill="#bbdefb" opacity="0.5">
      <animate attributeName="ry" values="5;6;5" dur="2s" repeatCount="indefinite" />
    </ellipse>
    {/* Rotation arc */}
    <path
      d="M18,32 A14,14 0 0,1 46,32"
      fill="none"
      stroke="#90caf9"
      strokeWidth="1.5"
      strokeDasharray="4,4"
      opacity="0.6"
    >
      <animate attributeName="stroke-dashoffset" from="0" to="16" dur="2s" repeatCount="indefinite" />
    </path>
  </Box>
);

const NeedleSVG = ({ size = 64 }) => (
  <Box as="svg" viewBox="0 0 64 64" w={`${size}px`} h={`${size}px`}>
    {/* Syringe barrel */}
    <rect x="18" y="24" width="24" height="16" rx="2" fill="none" stroke="#7e57c2" strokeWidth="2" />
    {/* Plunger */}
    <line x1="8" y1="32" x2="18" y2="32" stroke="#7e57c2" strokeWidth="2.5" strokeLinecap="round">
      <animate attributeName="x1" values="8;12;8" dur="3s" repeatCount="indefinite" />
    </line>
    <line x1="6" y1="26" x2="6" y2="38" stroke="#7e57c2" strokeWidth="2" strokeLinecap="round">
      <animate attributeName="x1" values="6;10;6" dur="3s" repeatCount="indefinite" />
      <animate attributeName="x2" values="6;10;6" dur="3s" repeatCount="indefinite" />
    </line>
    {/* Needle tip */}
    <path d="M42,28 L56,32 L42,36" fill="none" stroke="#b39ddb" strokeWidth="2" strokeLinejoin="round" />
    <line x1="56" y1="32" x2="60" y2="32" stroke="#b39ddb" strokeWidth="1.5" strokeLinecap="round" />
    {/* Measurement lines */}
    <line x1="24" y1="26" x2="24" y2="28" stroke="#7e57c2" strokeWidth="1" opacity="0.5" />
    <line x1="28" y1="26" x2="28" y2="28" stroke="#7e57c2" strokeWidth="1" opacity="0.5" />
    <line x1="32" y1="26" x2="32" y2="29" stroke="#7e57c2" strokeWidth="1.5" opacity="0.5" />
    <line x1="36" y1="26" x2="36" y2="28" stroke="#7e57c2" strokeWidth="1" opacity="0.5" />
    {/* Fluid drop */}
    <circle cx="60" cy="38" r="2" fill="#ce93d8" opacity="0.7">
      <animate attributeName="cy" values="36;44;36" dur="2s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.7;0;0.7" dur="2s" repeatCount="indefinite" />
    </circle>
  </Box>
);

// ─── Framer Motion Variants ─────────────────────────────────────────

/* Motion-on variants. Distances and durations match the shared system in
   components/motion so the home page and the rest of the site move the same
   way. Built per-render inside the component, because whether motion runs at
   all depends on the visitor's settings. */
const fadeUpMotion = {
  hidden: { opacity: 0, y: 26 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.85, ease: EASE },
  }),
};

const staggerMotion = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

/* Final position, no movement, for reduced-motion and the accessibility
   widget's "Pause Animations" switch. Content must still be visible. */
const settledVariants = {
  hidden: { opacity: 1, y: 0 },
  visible: { opacity: 1, y: 0, transition: { duration: 0 } },
};

// ─── Home Component ─────────────────────────────────────────────────

const Home = () => {
  const motionAllowed = useMotionAllowed();
  const fadeUp = motionAllowed ? fadeUpMotion : settledVariants;
  const staggerContainer = motionAllowed ? staggerMotion : settledVariants;


  const specialties = [
    { name: 'Orthopedics', SvgIcon: JointSVG, color: 'brand.500', path: '/specialties/orthopedics' },
    { name: 'Interventional Cardiology', SvgIcon: HeartPulseSVG, color: 'red.500', path: '/specialties/cardiovascular' },
    { name: 'Pain Management', SvgIcon: NeedleSVG, color: 'purple.500', path: '/specialties/pain-management' },
    { name: 'Spine', SvgIcon: SpineSVG, color: 'teal.500', path: '/specialties/spine' },
  ];

  const features = [
    {
      icon: FaStethoscope,
      title: 'Physician-First Environment',
      description: 'Built by surgeons, governed by surgeons, for surgeons. Data-driven operations that maximize your OR time and clinical autonomy.',
    },
    {
      icon: FaHospital,
      title: '30 State-of-the-Art ORs',
      description: 'Block time flexibility, with flip-room options. Advanced surgical suites with the latest technology, purpose-built for efficiency and outstanding outcomes.',
    },
    {
      icon: FaChartLine,
      title: 'Measurable Excellence',
      description: 'OR utilization rates above industry benchmarks with dedicated case managers supporting every procedure.',
    },
  ];

  // Core values, worded as supplied. Each statement follows the shared "WE"
  // lead-in, so that word is rendered once as a styled prefix rather than
  // repeated inside every string.
  const coreValues = [
    {
      icon: FaStar,
      title: 'Highest Standards',
      text: 'Hold ourselves to the Highest Standards and lead the industry in Patient Experience',
    },
    {
      icon: FaHandHoldingHeart,
      title: 'Kindness',
      text: 'Are Kind to Patients and fellow staff members',
    },
    {
      icon: FaBalanceScale,
      title: 'Honesty',
      text: 'Are Honest with ourselves and others',
    },
    {
      icon: FaGraduationCap,
      title: 'Learn-It-Alls',
      text: "Set EGO aside, and we are Learn-it-all's",
    },
    {
      icon: FaShieldAlt,
      title: 'Safety & Innovation',
      text: 'Are Innovators in securing patient Safety while progressing medical innovations',
    },
    {
      icon: FaClipboardCheck,
      title: 'Accountability',
      text: 'Are Accountable and take Responsibility for everything in our surroundings',
    },
    {
      icon: FaMedal,
      title: 'Earned Daily',
      text: 'Have no entitlement; everything is Earned Daily',
    },
    {
      icon: FaUsers,
      title: 'Team Players',
      text: 'Lift each other up, we are Team players, and we have fun',
    },
  ];

  return (
    <Box>

      {/* ── Hero Section (96vw rounded band). Starts flush at the top on large
             screens so the white header tab drops down into it. ── */}
      <Box pt={{ base: '76px', nav: 0 }}>
      <Box
        position="relative"
        minH={{ base: '560px', md: '660px', lg: '720px' }}
        overflow="hidden"
        borderRadius={{ base: '20px', md: '28px' }}
        w="96vw"
        ml="calc(50% - 48vw)"
      >
        {/* Background Image. HeroRecede drifts and grows it a few percent as
            the reader scrolls past, so the hero feels left behind rather than
            simply scrolled off. The overlay gradient rides with it. */}
        <HeroRecede
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bgImage="url('/hero-bg.jpg')"
          bgSize="cover"
          bgPosition="center"
          bgRepeat="no-repeat"
          _after={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            // A deep teal-ink scrim in place of the old cyan wash. Darkest
            // behind the type and along the bottom edge so white text holds
            // contrast, lighter elsewhere so the facility and its people are
            // actually visible. The photograph is the most credible thing on
            // the page; it should not be tinted into a flat brand color.
            bg: 'radial-gradient(ellipse 72% 62% at 50% 46%, rgba(14,47,58,0.58) 0%, rgba(14,47,58,0.22) 72%), linear-gradient(180deg, rgba(14,47,58,0.32) 0%, rgba(14,47,58,0.42) 55%, rgba(14,47,58,0.80) 100%)',
          }}
        />


        {/* Content */}
        <Container maxW="container.xl" h="100%" position="relative" zIndex={1}>
          <Flex
            minH={{ base: '560px', md: '660px', lg: '720px' }}
            align="center"
            justify="center"
            direction="column"
            textAlign="center"
            color="white"
            px={{ base: 4, md: 0 }}
            // Nudges the centered content down so it clears the header tab,
            // which grew with the larger price transparency button.
            pt={{ nav: '40px' }}
          >
            <MotionBox
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              maxW="800px"
            >
              <MotionBox variants={fadeUp} custom={0}>
                <HStack
                  display="inline-flex"
                  spacing={2.5}
                  px={4}
                  py={2}
                  mb={7}
                  borderRadius="pill"
                  sx={clearGlass}
                >
                  <Box w="6px" h="6px" borderRadius="full" bg="starOfLife.400" />
                  <Text
                    fontSize={{ base: 'xs', md: 'sm' }}
                    fontWeight="600"
                    textTransform="uppercase"
                    // Tighter tracking on phones keeps the chip to one line
                    // instead of stranding "ASC" on a second.
                    letterSpacing={{ base: '0.1em', md: '0.18em' }}
                    whiteSpace="nowrap"
                  >
                    Central Florida's Premier ASC
                  </Text>
                </HStack>
              </MotionBox>

              <Box>
                {/* The break after "Surgeons" is the author's. FitHeadline
                    holds it at every width by scaling the headline down
                    rather than letting the viewport choose where to wrap. */}
                <FitHeadline
                  lines={['Where Surgeons', 'Do Their Best Work']}
                  size={{ base: '2xl', md: '4xl' }}
                  color="white"
                  // Weight is what made this read cheap, not the typeface.
                  // Sora at 800 with tight tracking is loud; at 600 with a
                  // gentler squeeze it reads composed and expensive.
                  fontWeight="600"
                  lineHeight="1.06"
                  letterSpacing="-0.025em"
                  mb={7}
                  reveal
                  revealDelay={0.25}
                />
              </Box>

              <MotionBox variants={fadeUp} custom={2}>
                <Text
                  fontSize={{ base: 'lg', md: 'xl' }}
                  lineHeight="1.7"
                  opacity={0.92}
                  maxW="650px"
                  mx="auto"
                  mb={8}
                >
                  30 operating rooms. 19 specialties. A facility designed around physician
                  performance, operational efficiency, and exceptional patient outcomes.
                </Text>
              </MotionBox>

              <MotionBox variants={fadeUp} custom={3}>
                <HStack spacing={4} justify="center" flexWrap="wrap">
                  <Button
                    as={RouterLink}
                    to="/contact#physician-partnerships"
                    size="lg"
                    px={10}
                    py={7}
                    bg="white"
                    color="brand.900"
                    fontSize="lg"
                    fontWeight="600"
                    borderRadius="pill"
                    boxShadow="0 10px 30px -14px rgba(14,47,58,0.55)"
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: '0 16px 38px -14px rgba(14,47,58,0.6)',
                    }}
                    transition="all 0.25s"
                    rightIcon={<FaChevronRight />}
                    minW={{ base: 'full', sm: 'auto' }}
                  >
                    Physician Partnership
                  </Button>

                  <Button
                    as={RouterLink}
                    to="/about-facility"
                    size="lg"
                    px={10}
                    py={7}
                    sx={clearGlass}
                    color="white"
                    fontSize="lg"
                    fontWeight="600"
                    borderRadius="pill"
                    _hover={{
                      bg: 'rgba(255,255,255,0.18)',
                      transform: 'translateY(-2px)',
                    }}
                    transition="all 0.25s"
                    rightIcon={<FaChevronRight />}
                    minW={{ base: 'full', sm: 'auto' }}
                  >
                    Tour the Facility
                  </Button>
                </HStack>
              </MotionBox>
            </MotionBox>
          </Flex>
        </Container>

      </Box>
      </Box>

      {/* ── Value Proposition Cards ─────────────────────────────── */}
      {/* The cards ride up over the bottom of the hero so the glass has the
          photograph behind it to blur. Glass over a plain white page is just
          a white card with extra steps. */}
      <Container maxW="container.xl" pt={0} pb={{ base: 16, md: 20 }} mt={{ base: -10, md: -24 }} position="relative" zIndex={2}>
        <MotionBox
          as={Grid}
          templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
          gap={6}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          variants={staggerContainer}
        >
          {features.map((feature, index) => (
            <MotionBox key={index} variants={fadeUp} custom={index}>
              <VStack
                sx={lightGlass}
                p={{ base: 8, md: 10 }}
                borderRadius="24px"
                spacing={5}
                transition="transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)"
                _hover={{ transform: 'translateY(-3px)' }}
                h="100%"
                align="start"
              >
                {/* A hairline ring in place of the gradient tile. Filled,
                    shadowed gradient icon tiles are the single most common
                    template tell; a quiet mark reads more considered. */}
                <Flex
                  w="52px"
                  h="52px"
                  borderRadius="full"
                  align="center"
                  justify="center"
                  bg="brand.50"
                  border="1px solid"
                  borderColor="rgba(14,47,58,0.08)"
                >
                  <Icon as={feature.icon} boxSize={5} color="brand.600" />
                </Flex>
                <Heading size="md" textAlign="left" color="ink.900" fontWeight="600" letterSpacing="-0.01em">
                  {feature.title}
                </Heading>
                <Text color="ink.600" textAlign="left" lineHeight="1.62" fontSize="md">
                  {feature.description}
                </Text>
              </VStack>
            </MotionBox>
          ))}
        </MotionBox>
      </Container>

      {/* ── Physician Partnership CTA (edge-to-edge split) ──────── */}
      {/* The home page's lead argument for physicians, kept as the first full
          section after the value cards. The benefits arrive one at a time so
          each one is actually read, and the photograph settles out of a
          slight zoom rather than sitting flat. */}
      <Box bg="white" pt={{ base: 4, md: 8 }}>
        <Grid
          // minmax(0, 1fr), not 1fr: a plain 1fr track grows to the min-content
          // width of the nowrap headline, so the headline would fit itself to the
          // widened track instead of the screen and push the page sideways.
          templateColumns={{ base: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' }}
          alignItems="stretch"
          minH={{ lg: '640px' }}
        >
          <GridItem
            display="flex"
            alignItems="center"
            justifyContent={{ lg: 'flex-end' }}
            px={{ base: 6, md: 10, lg: 16 }}
            py={{ base: 12, lg: 20 }}
          >
            <VStack align="start" spacing={6} maxW="600px" w="100%">
              <Reveal>
                <HStack spacing={2.5}>
                  <Box w="6px" h="6px" borderRadius="full" bg="starOfLife.500" />
                  <Text
                    fontSize="xs"
                    fontWeight="600"
                    textTransform="uppercase"
                    letterSpacing="0.18em"
                    color="starOfLife.700"
                  >
                    For Physicians
                  </Text>
                </HStack>
              </Reveal>

              <Reveal w="100%">
                <FitHeadline
                  as="h2"
                  lines={['A partnership built around', 'your clinical vision']}
                  size={{ base: 'xl', md: '2xl' }}
                  color="ink.900"
                  fontWeight="600"
                  lineHeight="1.15"
                  letterSpacing="-0.022em"
                />
              </Reveal>

              <Reveal>
                <Text fontSize="lg" color="ink.600" lineHeight="1.7">
                  Lake Lucien was designed from the ground up to support surgeon autonomy
                  and efficiency. From our 30 purpose-built ORs to our data-driven scheduling,
                  every detail serves your practice.
                </Text>
              </Reveal>

              <RevealGroup w="100%">
                <VStack align="start" spacing={3.5} pt={2}>
                  {[
                    'Block time flexibility across 30 ORs',
                    'Dedicated case management and OR support staff',
                    'Teaching facility with conference center and training OR',
                    'Outdoor ERAS recovery programs',
                    'Full practice support from a physician-centric team',
                  ].map((item, i) => (
                    <RevealItem key={i}>
                      <HStack align="start" spacing={3.5}>
                        <Flex
                          w="22px"
                          h="22px"
                          mt="1px"
                          flexShrink={0}
                          borderRadius="full"
                          align="center"
                          justify="center"
                          bg="brand.50"
                          border="1px solid"
                          borderColor="rgba(14,47,58,0.10)"
                        >
                          <Icon as={FaCheck} boxSize="9px" color="brand.600" />
                        </Flex>
                        <Text fontSize="md" color="ink.700" lineHeight="1.55">
                          {item}
                        </Text>
                      </HStack>
                    </RevealItem>
                  ))}
                </VStack>
              </RevealGroup>

              <Reveal>
                <Button
                  as={RouterLink}
                  to="/contact#physician-partnerships"
                  size="lg"
                  px={8}
                  py={7}
                  mt={2}
                  variant="brandGradient"
                  rightIcon={<FaChevronRight />}
                >
                  Explore Partnership
                </Button>
              </Reveal>
            </VStack>
          </GridItem>

          {/* Inset with a soft radius instead of bleeding to the edge, and no
              cyan tint laid over it: the photograph is shown as it is. */}
          <GridItem position="relative" minH={{ base: '340px', md: '460px', lg: 'auto' }}>
            <ZoomSettle
              position="absolute"
              top={{ base: 4, lg: 6 }}
              right={{ base: 4, lg: 6 }}
              bottom={{ base: 4, lg: 6 }}
              left={{ base: 4, lg: 0 }}
              borderRadius="28px"
            >
              <Box
                h="100%"
                w="100%"
                bgImage="url('/facility/operating-room.jpg')"
                bgSize="cover"
                bgPosition="center"
                role="img"
                aria-label="Lake Lucien Surgery Center operating room"
              />
            </ZoomSettle>
          </GridItem>
        </Grid>
      </Box>

      {/* ── Specialties Section ──────────────────────────────────── */}
      {/* A mist sheet that slides up over the partnership section. The
          specialty cards cascade in so the four read as a set. */}
      <LightSheet tone="mist">
        <Container maxW="container.xl">
          <SectionHeading
            eyebrow="Surgical Services"
            title="Primary Specialties"
            lede="Comprehensive surgical services across four core specialty areas"
          />

          <RevealGroup>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 4, md: 6 }} w="100%">
              {specialties.map((specialty, index) => (
                <RevealItem key={index} h="100%">
                  <Box
                    as={RouterLink}
                    to={specialty.path}
                    display="block"
                    h="100%"
                    bg="white"
                    p={{ base: 6, md: 8 }}
                    borderRadius="24px"
                    boxShadow="cardSoft"
                    border="1px solid"
                    borderColor="rgba(14,47,58,0.06)"
                    textAlign="center"
                    transition="transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.5s ease"
                    _hover={{ transform: 'translateY(-3px)', boxShadow: 'card' }}
                    _focusVisible={{ outline: '2px solid', outlineColor: 'brand.600', outlineOffset: '3px' }}
                  >
                    <Flex justify="center" mb={4}>
                      <specialty.SvgIcon size={56} />
                    </Flex>
                    <Heading size="sm" color="ink.900" fontWeight="600">
                      {specialty.name}
                    </Heading>
                  </Box>
                </RevealItem>
              ))}
            </SimpleGrid>
          </RevealGroup>

          <Reveal textAlign="center" mt={12}>
            <Button
              as={RouterLink}
              to="/specialties"
              size="lg"
              px={8}
              variant="brandGradient"
              rightIcon={<FaChevronRight />}
            >
              View All Specialties
            </Button>
          </Reveal>
        </Container>
      </LightSheet>

      {/* ── Partnership / Innovation Center CTA ──────────────────── */}
      {/* Deep ink with the building far in the background, sliding up over
          the specialties sheet. The two confirmed figures count up; the third
          is words, so it stays words. */}
      <InkBand image="/facility/exterior.jpg">
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <SectionHeading
              tone="dark"
              eyebrow="Physician Partnerships"
              title="Partner with the region's largest and most innovative ambulatory surgery center"
              lede="Be part of something extraordinary as we redefine surgical excellence. Planning investment across 19 specialties to ensure each department has the tools and support for excellence."
              maxW="3xl"
              mb={4}
            />

            <RevealGroup w="100%" maxW="3xl">
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 4, md: 5 }} py={4}>
                {[
                  { stat: '30', label: 'Operating Rooms' },
                  { stat: '19', label: 'Specialties' },
                  { stat: 'Innovation', label: 'Leader' },
                ].map((item, i) => (
                  <RevealItem key={i}>
                    <VStack spacing={1} py={7} px={4} borderRadius="20px" sx={clearGlass}>
                      <Heading
                        size="2xl"
                        color="white"
                        fontWeight="600"
                        letterSpacing="-0.03em"
                        // A word needs a smaller size than a two-digit figure to
                        // stay inside its tile. The line height matches the
                        // figures' so all three tiles keep the same height.
                        {...(/^\d+$/.test(item.stat)
                          ? {}
                          : {
                              fontSize: { base: '4xl', md: '3xl', lg: '4xl' },
                              lineHeight: { base: 1.2, md: '44px' },
                            })}
                      >
                        {/* Only the confirmed figures count up. "Innovation"
                            is not a number, and animating a figure implies a
                            precision that wording does not carry. */}
                        {/^\d+$/.test(item.stat) ? (
                          <CountUp to={Number(item.stat)} />
                        ) : (
                          item.stat
                        )}
                      </Heading>
                      <Text
                        fontSize="sm"
                        color="whiteAlpha.800"
                        fontWeight="600"
                        textTransform="uppercase"
                        letterSpacing="0.12em"
                      >
                        {item.label}
                      </Text>
                    </VStack>
                  </RevealItem>
                ))}
              </SimpleGrid>
            </RevealGroup>

            <Reveal>
              <Button
                as={RouterLink}
                to="/sponsors"
                size="lg"
                px={8}
                variant="brandOnDark"
                rightIcon={<FaChevronRight />}
              >
                Become a Founding Partner
              </Button>
            </Reveal>
          </VStack>
        </Container>
      </InkBand>

      {/* ── Physician Partnership Form ───────────────────────────── */}
      <PhysicianPartnershipForm />

      {/* ── About / Facility Section (edge-to-edge, image left) ──── */}
      {/* Mirror of the partnership split: photograph first, reasons second,
          each reason arriving in turn. */}
      <Box bg="white" py={{ base: 4, md: 8 }}>
        <Grid
          // minmax(0, 1fr), not 1fr: a plain 1fr track grows to the min-content
          // width of the nowrap headline, so the headline would fit itself to the
          // widened track instead of the screen and push the page sideways.
          templateColumns={{ base: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' }}
          alignItems="stretch"
          minH={{ lg: '660px' }}
        >
          <GridItem
            order={{ base: 2, lg: 2 }}
            display="flex"
            alignItems="center"
            px={{ base: 6, md: 10, lg: 16 }}
            py={{ base: 12, lg: 20 }}
          >
            <VStack align="start" spacing={6} maxW="600px" w="100%">
              <Reveal>
                <HStack spacing={2.5}>
                  <Box w="6px" h="6px" borderRadius="full" bg="starOfLife.500" />
                  <Text
                    fontSize="xs"
                    fontWeight="600"
                    textTransform="uppercase"
                    letterSpacing="0.18em"
                    color="starOfLife.700"
                  >
                    Our Facility
                  </Text>
                </HStack>
              </Reveal>

              <Reveal w="100%">
                <FitHeadline
                  as="h2"
                  lines={['Why Choose Lake Lucien?']}
                  size={{ base: 'xl', md: '2xl' }}
                  color="ink.900"
                  fontWeight="600"
                  lineHeight="1.15"
                  letterSpacing="-0.022em"
                />
              </Reveal>

              <RevealGroup w="100%">
                <VStack align="start" spacing={4}>
                  {[
                    { label: 'Personalized Care', text: 'Dedicated concierge-style case managers guide you from pre-op to post-op' },
                    { label: 'Safety & Healing', text: 'Highest standards of infection control with calming digital scenes in recovery' },
                    { label: 'Efficiency & Comfort', text: 'Quick OR turnover minimizes wait times for a smooth experience' },
                    { label: 'Innovation', text: 'Exploring outdoor ERAS programs and advanced recovery pathways' },
                  ].map((item, i) => (
                    <RevealItem key={i}>
                      <HStack align="start" spacing={4}>
                        <Flex
                          w="22px"
                          h="22px"
                          mt="3px"
                          flexShrink={0}
                          borderRadius="full"
                          align="center"
                          justify="center"
                          bg="brand.50"
                          border="1px solid"
                          borderColor="rgba(14,47,58,0.10)"
                        >
                          <Icon as={FaCheck} boxSize="9px" color="brand.600" />
                        </Flex>
                        <Text fontSize="lg" color="ink.700" lineHeight="1.55">
                          <Text as="strong" fontWeight="600" color="ink.900">{item.label}:</Text> {item.text}
                        </Text>
                      </HStack>
                    </RevealItem>
                  ))}
                </VStack>
              </RevealGroup>

              <Reveal>
                <Button
                  as={RouterLink}
                  to="/about-facility"
                  size="lg"
                  px={8}
                  mt={2}
                  variant="brandGradient"
                  rightIcon={<FaChevronRight />}
                >
                  Learn About Our Facility
                </Button>
              </Reveal>
            </VStack>
          </GridItem>

          <GridItem
            order={{ base: 1, lg: 1 }}
            position="relative"
            minH={{ base: '340px', md: '460px', lg: 'auto' }}
          >
            <ZoomSettle
              position="absolute"
              top={{ base: 4, lg: 6 }}
              right={{ base: 4, lg: 0 }}
              bottom={{ base: 4, lg: 6 }}
              left={{ base: 4, lg: 6 }}
              borderRadius="28px"
            >
              <Box
                h="100%"
                w="100%"
                bgImage="url('/facility/exterior.jpg')"
                bgSize="cover"
                bgPosition="center"
                role="img"
                aria-label="Lake Lucien Surgery Center exterior"
              />
            </ZoomSettle>
          </GridItem>
        </Grid>
      </Box>

      {/* ── Core Values ──────────────────────────────────────────── */}
      <LightSheet tone="mist">
        <Container maxW="container.xl">
          <SectionHeading eyebrow="Core Values" title="How we show up, every day" />

          <RevealGroup>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={{ base: 4, md: 6 }} w="100%">
              {coreValues.map((value) => (
                <RevealItem key={value.title} h="100%">
                  <VStack
                    bg="white"
                    p={{ base: 6, md: 7 }}
                    borderRadius="24px"
                    boxShadow="cardSoft"
                    border="1px solid"
                    borderColor="rgba(14,47,58,0.06)"
                    spacing={4}
                    align="start"
                    h="100%"
                    transition="transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.5s ease"
                    _hover={{ transform: 'translateY(-3px)', boxShadow: 'card' }}
                  >
                    <IconMark icon={value.icon} />
                    <Heading size="sm" color="ink.900" fontWeight="600">
                      {value.title}
                    </Heading>
                    <Text color="ink.600" lineHeight="1.62" fontSize="sm">
                      <Text
                        as="span"
                        fontWeight="700"
                        color="starOfLife.700"
                        letterSpacing="0.06em"
                      >
                        WE
                      </Text>{' '}
                      {value.text}
                    </Text>
                  </VStack>
                </RevealItem>
              ))}
            </SimpleGrid>
          </RevealGroup>

          {/* The closing line of the values, set as a quiet ink card instead of
              a gradient banner with a dot texture. */}
          <Reveal mt={{ base: 8, md: 10 }}>
            <Box
              bg="brand.900"
              borderRadius="24px"
              py={{ base: 8, md: 10 }}
              px={{ base: 6, md: 10 }}
              textAlign="center"
              boxShadow="card"
            >
              <Text
                color="white"
                fontSize={{ base: 'lg', md: 'xl' }}
                fontWeight="500"
                lineHeight="1.6"
              >
                DAILY demonstration of these values is how we build{' '}
                <Text as="span" fontWeight="700" letterSpacing="0.08em" color="starOfLife.300">
                  TRUST
                </Text>
              </Text>
            </Box>
          </Reveal>
        </Container>
      </LightSheet>

      {/* ── Bottom CTA ───────────────────────────────────────────── */}
      <InkBand>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <SectionHeading
              tone="dark"
              title="Ready to Learn More?"
              lede="Whether you're a physician exploring partnership or a patient seeking exceptional care, we'd love to connect."
              mb={2}
            />
            <Reveal>
              <HStack spacing={4} pt={2} flexWrap="wrap" justify="center">
                <Button
                  as={RouterLink}
                  to="/contact"
                  size="lg"
                  px={10}
                  py={7}
                  fontSize="lg"
                  variant="brandOnDark"
                  rightIcon={<FaChevronRight />}
                >
                  Contact Us
                </Button>

                <Button
                  as="a"
                  href="tel:+13213213935"
                  size="lg"
                  px={10}
                  py={7}
                  sx={clearGlass}
                  color="white"
                  fontSize="lg"
                  fontWeight="600"
                  borderRadius="pill"
                  _hover={{ bg: 'rgba(255,255,255,0.18)', transform: 'translateY(-2px)' }}
                  transition="transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), background-color 0.2s"
                >
                  Call (321) 321-3935
                </Button>
              </HStack>
            </Reveal>
          </VStack>
        </Container>
      </InkBand>
    </Box>
  );
};

export default Home;
